import AsyncStorage from '@react-native-async-storage/async-storage';
// PV2-21 / CL-P10: hot-path cache logs go through safeLog.
// safeLog.info / safeLog.debug are no-ops in production; only errors are persisted.
const { safeLog } = require('../utils/safeLog.js');

/**
 * Unified Cache Service
 * Provides multi-layer caching (memory -> AsyncStorage -> API) with TTL and invalidation.
 *
 * PV2-22 (CL-P02, CL-P23): Stale-While-Revalidate (SWR) primitive (PP-03).
 * Pass { swr: true } to get() to return stale data immediately while a background
 * refresh runs silently. Cold misses (no entry at all) always await the fetcher.
 * Background revalidation errors are swallowed — the stale value remains valid
 * until the next successful refresh. A dedup guard (inFlightSWR) prevents
 * concurrent background fetches for the same key.
 */

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
  version: string; // For cache versioning
}

export interface CacheOptions {
  ttl?: number; // Default TTL in milliseconds
  version?: string; // Cache version for invalidation
  skipMemory?: boolean; // Skip memory cache layer
  skipStorage?: boolean; // Skip AsyncStorage layer
  swr?: boolean; // Return stale data immediately and revalidate in background (PP-03)
}

export interface CacheStats {
  memoryHits: number;
  storageHits: number;
  misses: number;
  lastCleanup: number;
}

class CacheService {
  private memoryCache = new Map<string, CacheEntry<any>>();
  // PV2-22: dedup guard — prevents concurrent background SWR fetches for the same key.
  private readonly inFlightSWR = new Set<string>();

  private stats: CacheStats = {
    memoryHits: 0,
    storageHits: 0,
    misses: 0,
    lastCleanup: Date.now()
  };

  // Default cache configurations
  private readonly DEFAULT_TTL = 15 * 60 * 1000; // 15 minutes
  private readonly SHORT_TTL = 2 * 60 * 1000; // 2 minutes
  private readonly LONG_TTL = 60 * 60 * 1000; // 1 hour
  private readonly VERY_LONG_TTL = 24 * 60 * 60 * 1000; // 24 hours
  
  // Cache key prefixes for organization
  private readonly PREFIXES = {
    USER: 'user:',
    CLINIC: 'clinic:',
    BILL_CONFIG: 'bill_config:',
    NOTIFICATIONS: 'notifications:',
    APP_CONFIG: 'app_config:',
    PLAN: 'plan:',
    PROFILE_PHOTOS: 'profile_photos:'
  };

  // Cleanup interval (run every 10 minutes)
  private readonly CLEANUP_INTERVAL = 10 * 60 * 1000;
  private cleanupTimer?: ReturnType<typeof setInterval>;

  constructor() {
    this.startCleanupTimer();
  }

  /**
   * Get data from cache with fallback to fetcher function.
   *
   * Behaviour by path:
   *   swr: false (default)
   *     - Fresh hit  → return synchronously (no network).
   *     - Stale hit  → evict, await fetcher, store, return.
   *     - Cold miss  → await fetcher, store, return.
   *
   *   swr: true (PP-03 stale-while-revalidate)
   *     - Fresh hit  → return synchronously (no network, no spinner).
   *     - Stale hit  → return stale data immediately (no spinner), queue background refresh.
   *     - Cold miss  → await fetcher synchronously (spinner acceptable — no data yet).
   */
  async get<T>(
    key: string, 
    fetcher: () => Promise<T>, 
    options: CacheOptions = {}
  ): Promise<T> {
    const fullKey = this.buildKey(key);
    const ttl = options.ttl || this.DEFAULT_TTL;
    const version = options.version || '1.0';
    const swr = options.swr ?? false;

    try {
      // Layer 1: Check memory cache
      if (!options.skipMemory) {
        const rawEntry = this.memoryCache.get(fullKey);
        if (rawEntry && rawEntry.version === version) {
          const age = Date.now() - rawEntry.timestamp;
          if (age <= ttl) {
            this.stats.memoryHits++;
            safeLog.debug(`🎯 Cache: Memory hit for ${key}`);
            return rawEntry.data;
          }
          if (swr) {
            // CL-P23: stale data returned instantly — no spinner.
            this.stats.memoryHits++;
            safeLog.debug(`🔄 Cache: SWR stale memory for ${key}, background refresh queued`);
            this.revalidateInBackground(key, fullKey, fetcher, options);
            return rawEntry.data;
          }
          // Not SWR: evict stale entry and fall through
          this.memoryCache.delete(fullKey);
        }
      }

      // Layer 2: Check AsyncStorage
      if (!options.skipStorage) {
        const rawStorageEntry = await this.getRawFromStorage<T>(fullKey);
        if (rawStorageEntry && rawStorageEntry.version === version) {
          const age = Date.now() - rawStorageEntry.timestamp;
          if (age <= ttl) {
            this.stats.storageHits++;
            safeLog.debug(`💾 Cache: Storage hit for ${key}`);
            if (!options.skipMemory) {
              this.setInMemory(fullKey, rawStorageEntry.data, ttl, version);
            }
            return rawStorageEntry.data;
          }
          if (swr) {
            // CL-P23: stale data returned instantly — no spinner.
            this.stats.storageHits++;
            safeLog.debug(`🔄 Cache: SWR stale storage for ${key}, background refresh queued`);
            // Promote stale value to memory so subsequent SWR reads skip storage I/O.
            if (!options.skipMemory) {
              this.setInMemory(fullKey, rawStorageEntry.data, ttl, version);
            }
            this.revalidateInBackground(key, fullKey, fetcher, options);
            return rawStorageEntry.data;
          }
          // Not SWR: evict stale entry from storage
          await AsyncStorage.removeItem(fullKey);
        }
      }

      // Layer 3: Cold miss — fetch synchronously (spinner acceptable — no cached data exists)
      safeLog.debug(`🌐 Cache: Miss for ${key}, fetching from source`);
      this.stats.misses++;
      const data = await fetcher();
      await this.set(key, data, options);
      return data;

    } catch (error) {
      // Distinguish fetcher errors from cache-layer errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isFetcherError = errorMessage.includes('fetch') || 
                             errorMessage.includes('API') || 
                             errorMessage.includes('Profile') ||
                             errorMessage.includes('Clinic') ||
                             errorMessage.includes('not found');
      
      if (isFetcherError) {
        safeLog.error(`❌ Cache: Fetcher error for ${key}:`, error);
        throw error;
      }
      
      safeLog.error(`❌ Cache: Cache operation error for ${key}:`, error);
      try {
        return await fetcher();
      } catch (fetchError) {
        safeLog.error(`❌ Cache: Fetcher also failed for ${key}:`, fetchError);
        throw fetchError;
      }
    }
  }

  /**
   * PP-03: SWR background revalidation.
   * Errors are swallowed — stale value remains valid until the next successful refresh.
   * inFlightSWR dedup guard ensures at most one background fetch per key at a time.
   */
  private revalidateInBackground<T>(
    key: string,
    fullKey: string,
    fetcher: () => Promise<T>,
    options: CacheOptions
  ): void {
    if (this.inFlightSWR.has(fullKey)) return;
    this.inFlightSWR.add(fullKey);
    (async () => {
      try {
        const data = await fetcher();
        await this.set(key, data, options);
        safeLog.debug(`✅ Cache: SWR background refresh completed for ${key}`);
      } catch {
        safeLog.warn(`⚠️ Cache: SWR background refresh failed for ${key}, stale value retained`);
      } finally {
        this.inFlightSWR.delete(fullKey);
      }
    })();
  }

  /**
   * Set data in cache
   */
  async set<T>(key: string, data: T, options: CacheOptions = {}): Promise<void> {
    const fullKey = this.buildKey(key);
    const ttl = options.ttl || this.DEFAULT_TTL;
    const version = options.version || '1.0';

    try {
      if (!options.skipMemory) {
        this.setInMemory(fullKey, data, ttl, version);
      }
      if (!options.skipStorage) {
        await this.setInStorage(fullKey, data, ttl, version);
      }
      safeLog.debug(`✅ Cache: Set ${key} with TTL ${ttl}ms`);
    } catch (error) {
      safeLog.error(`❌ Cache: Error setting ${key}:`, error);
    }
  }

  /**
   * Invalidate specific cache key (CL-P11)
   */
  async invalidate(key: string): Promise<void> {
    const fullKey = this.buildKey(key);
    try {
      this.memoryCache.delete(fullKey);
      await AsyncStorage.removeItem(fullKey);
      safeLog.debug(`🗑️ Cache: Invalidated ${key}`);
    } catch (error) {
      safeLog.error(`❌ Cache: Error invalidating ${key}:`, error);
    }
  }

  /**
   * Invalidate cache by pattern
   */
  async invalidatePattern(pattern: string): Promise<void> {
    try {
      for (const key of this.memoryCache.keys()) {
        if (key.includes(pattern)) {
          this.memoryCache.delete(key);
        }
      }
      const allKeys = await AsyncStorage.getAllKeys();
      const matchingKeys = allKeys.filter(key => key.includes(pattern));
      if (matchingKeys.length > 0) {
        await AsyncStorage.multiRemove(matchingKeys);
      }
      safeLog.debug(`🗑️ Cache: Invalidated pattern "${pattern}"`);
    } catch (error) {
      safeLog.error(`❌ Cache: Error invalidating pattern ${pattern}:`, error);
    }
  }

  /**
   * Get specific cache configurations for different data types
   */
  getConfig(type: keyof typeof this.PREFIXES): CacheOptions {
    switch (type) {
      case 'USER':
        return { ttl: this.LONG_TTL, version: '1.0' };
      case 'CLINIC':
        return { ttl: this.VERY_LONG_TTL, version: '1.0' };
      case 'BILL_CONFIG':
        return { ttl: this.LONG_TTL, version: '1.0' };
      case 'NOTIFICATIONS':
        return { ttl: this.SHORT_TTL, version: '1.0' };
      case 'APP_CONFIG':
        return { ttl: this.VERY_LONG_TTL, version: '1.0' };
      case 'PLAN':
        return { ttl: this.DEFAULT_TTL, version: '1.0' };
      case 'PROFILE_PHOTOS':
        return { ttl: this.VERY_LONG_TTL, version: '1.0' };
      default:
        return { ttl: this.DEFAULT_TTL, version: '1.0' };
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats & { memorySize: number } {
    return {
      ...this.stats,
      memorySize: this.memoryCache.size
    };
  }

  /**
   * Clear all caches
   */
  async clearAll(): Promise<void> {
    try {
      this.memoryCache.clear();
      const allKeys = await AsyncStorage.getAllKeys();
      const cacheKeys = allKeys.filter(key => 
        Object.values(this.PREFIXES).some(prefix => key.startsWith(`@padrinho_cache_${prefix}`))
      );
      if (cacheKeys.length > 0) {
        await AsyncStorage.multiRemove(cacheKeys);
      }
      safeLog.debug(`🗑️ Cache: Cleared all caches`);
    } catch (error) {
      safeLog.error('❌ Cache: Error clearing all caches:', error);
    }
  }

  /**
   * Private helper methods
   */
  private buildKey(key: string): string {
    return `@padrinho_cache_${key}`;
  }

  /**
   * Read a raw storage entry without expiry/version filtering.
   * Used by the SWR path to check for stale entries before deciding to revalidate.
   */
  private async getRawFromStorage<T>(fullKey: string): Promise<CacheEntry<T> | null> {
    try {
      const stored = await AsyncStorage.getItem(fullKey);
      if (!stored) return null;
      return JSON.parse(stored) as CacheEntry<T>;
    } catch {
      return null;
    }
  }

  private getFromMemory<T>(key: string, ttl: number, version: string): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;
    const now = Date.now();
    const isExpired = now - entry.timestamp > ttl;
    const isWrongVersion = entry.version !== version;
    if (isExpired || isWrongVersion) {
      this.memoryCache.delete(key);
      return null;
    }
    return entry.data;
  }

  private async getFromStorage<T>(key: string, ttl: number, version: string): Promise<T | null> {
    try {
      const stored = await AsyncStorage.getItem(key);
      if (!stored) return null;
      const entry: CacheEntry<T> = JSON.parse(stored);
      const now = Date.now();
      const isExpired = now - entry.timestamp > ttl;
      const isWrongVersion = entry.version !== version;
      if (isExpired || isWrongVersion) {
        await AsyncStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch (error) {
      safeLog.error(`❌ Cache: Error reading from storage for ${key}:`, error);
      return null;
    }
  }

  private setInMemory<T>(key: string, data: T, ttl: number, version: string): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version
    };
    this.memoryCache.set(key, entry);
  }

  private async setInStorage<T>(key: string, data: T, ttl: number, version: string): Promise<void> {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      version
    };
    await AsyncStorage.setItem(key, JSON.stringify(entry));
  }

  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }

  private cleanup(): void {
    const now = Date.now();
    let removedCount = 0;
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.memoryCache.delete(key);
        removedCount++;
      }
    }
    this.stats.lastCleanup = now;
    if (removedCount > 0) {
      safeLog.debug(`🧹 Cache: Cleaned up expired entries`);
    }
  }

  /**
   * Destroy cache service (cleanup timers)
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.memoryCache.clear();
  }
}

// Export singleton instance
export const cacheService = new CacheService();
export default cacheService;
